import { isNotDefined } from '@sgme/fp';
import { GetRowIdParams } from 'ag-grid-community';
import { CustomCellRendererProps } from 'ag-grid-react';
import { useClientSideConnect } from 'components/Blotters/Trades/Grid/connect/useClientSideConnect';
import { useValueWithUnitColumnType } from 'components/Blotters/Trades/Grid/connect/useValueWithUnitColumn';
import { getStatusFieldColor } from 'components/shared/Blotter/BlotterPanel/Fields/StatusField/StatusField';
import { ClientSideBlotterGrid } from 'components/shared/Blotter/Grid/client-side-blotter';
import { useClientSideDefaultColumnTypes } from 'components/shared/Blotter/Grid/Hooks/useClientSideDefaultColumnTypes';
import { useDefaultColumnDefinition } from 'components/shared/Blotter/Grid/Hooks/useDefaultColumnDefinition';
import { useServerSideStatusColumnType } from 'components/shared/Blotter/Grid/Hooks/useStatusColumnType';
import { getHeaderForBlotterIntlKey } from 'components/shared/Blotter/utils';
import { LightAlert } from 'components/shared/LightAlert';
import { useAppSelector } from 'hooks/useAppSelector';
import { useWorkflowErrorsUtils } from 'hooks/useWorkflowErrorsUtils';
import { useEffect, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch } from 'react-redux';
import { tradesBlottersItemsRequested } from 'state/blotters/trades/tradesBlotters.actions';
import {
  selectMasterDetailsErrorContent,
  selectTradesBlottersColumnDefinition,
  selectTradesBlottersError,
} from 'state/blotters/trades/tradesBlotters.selector';
import { DlpProductBlotterItem, DlpProductTradeItem } from 'state/blotters/trades/types/blotterItem';

const getRowId = (row: GetRowIdParams<DlpProductTradeItem>) => row.data.tradeReference ?? '';
const getHeaderIntlKey = getHeaderForBlotterIntlKey('dlpProductTrades');

export function useDlpTradesBlottersProps() {
  const defaultColumnDef = useDefaultColumnDefinition<DlpProductTradeItem>(getHeaderIntlKey);

  const statusColumn = useServerSideStatusColumnType(getStatusFieldColor);
  const valueWithUnitColumnType = useValueWithUnitColumnType();
  const customCols = useMemo(
    () => ({
      status: statusColumn,
      valueWithUnit: valueWithUnitColumnType,
    }),
    [statusColumn, valueWithUnitColumnType],
  );
  const columnTypes = useClientSideDefaultColumnTypes<any>(customCols);

  return { defaultColumnDef, columnTypes };
}

export function DlpProductTradeGrid({ data }: CustomCellRendererProps<DlpProductBlotterItem>) {
  const { columnTypes, defaultColumnDef } = useDlpTradesBlottersProps();
  const connectedProps = useClientSideConnect({
    blotter: 'dlpProductTrades',
    idKey: 'tradeReference',
    hasSidePanel: true,
    parentId: data?.isin as string,
  });

  const dispatch = useDispatch();
  const { errorsToString } = useWorkflowErrorsUtils();
  const columnDefinitions = useAppSelector(state => selectTradesBlottersColumnDefinition(state, 'dlpProductTrades'));
  const stringData = JSON.stringify(data);
  const hasError = useAppSelector(state => selectTradesBlottersError(state, 'dlpProductTrades', data?.isin as string));

  const errors = errorsToString(
    useAppSelector(state => selectMasterDetailsErrorContent(state, 'dlpProductTrades', data?.isin as string)),
  );
  useEffect(() => {
    if (isNotDefined(data?.isin) || isNotDefined(stringData)) {
      return;
    }
    dispatch(tradesBlottersItemsRequested('dlpProductTrades', data.isin));
  }, [data?.isin, dispatch, stringData]);

  const height = Math.max(connectedProps.items?.length ?? 0, 5) * 48 + 59;

  return (
    <div className="p-5">
      {hasError ? (
        <LightAlert severity="danger">
          {isNotDefined(errors) ? <FormattedMessage id="dlpProductTrades.loadError" /> : errors}
        </LightAlert>
      ) : (
        <div style={{ height }}>
          <ClientSideBlotterGrid<DlpProductTradeItem>
            {...connectedProps}
            e2e="dlp-product-trades"
            name="dlpProductTrades"
            logAnalyticName="Blotter My flow products trades"
            getRowId={getRowId}
            defaultCsvExportParams={{ fileName: 'dlpProductTrades' }}
            autoSizeStrategy={{ type: 'fitGridWidth' }}
            columnTypes={columnTypes}
            defaultColDef={defaultColumnDef}
            columnDefinitions={columnDefinitions}
          />
        </div>
      )}
    </div>
  );
}
